/**
 * Enhanced Football Service with RTDB Caching
 * Wraps the existing FootballService with RTDB cache layer
 */

import FootballService from '../../../../components/dashboards/services/footballServiceExtended';
import rtdbCache from './rtdbCache';
import {
  CACHE_PATHS,
  getCacheKey,
  getTTLForDataType,
  getDynamicTTL,
  logCache,
  RTDB_FEATURES
} from './rtdbConfig';

class RTDBFootballService {
  constructor() {
    this.baseService = FootballService;
    this.cache = rtdbCache;
  }

  /**
   * Get fixtures with RTDB caching and dynamic TTL
   */
  async getFixtures(
    id = null, ids = null, live = null, date = null, league = null,
    season = null, team = null, last = null, next = null, from = null,
    to = null, round = null, status = null, venue = null, timezone = null
  ) {
    // Determine cache key
    const isLive = live === 'all' || !!live;
    let cacheKey;

    console.log('🔑 [rtdbFootballService.getFixtures] Called with:', {
      date, league, season, live, team
    });

    if (isLive) {
      cacheKey = `${CACHE_PATHS.FIXTURES}/live`;
    } else if (date && league) {
      // Include season in cache key when league is specified
      // This prevents wrong season data from being cached/retrieved
      const seasonPart = season ? `_${season}` : '';
      cacheKey = `${CACHE_PATHS.FIXTURES}/${date}/${getCacheKey('league', league)}${seasonPart}`;
    } else if (date) {
      cacheKey = `${CACHE_PATHS.FIXTURES}/${date}/all`;
    } else {
      // Complex query, don't cache
      return this.baseService.getFixtures(
        id, ids, live, date, league, season, team, last, next,
        from, to, round, status, venue, timezone
      );
    }

    console.log('🔑 [rtdbFootballService] Using cache key:', cacheKey);

    // Wrapper function that determines TTL based on fetched data
    const fetchWithDynamicTTL = async () => {
      const result = await this.baseService.getFixtures(
        id, ids, live, date, league, season, team, last, next,
        from, to, round, status, venue, timezone
      );

      // Determine TTL based on the actual fixture data
      let ttl;
      if (RTDB_FEATURES.USE_DYNAMIC_TTL && result?.response?.length > 0) {
        // Find the most relevant fixture for TTL calculation
        const fixtures = result.response;

        // Priority: Live games > Starting soon > Today > Tomorrow > Future
        const liveGame = fixtures.find(f =>
          f.fixture?.status?.short === 'LIVE' ||
          f.fixture?.status?.short === '1H' ||
          f.fixture?.status?.short === '2H'
        );

        if (liveGame) {
          ttl = getDynamicTTL(liveGame, 'fixtures');
          logCache('info', `Dynamic TTL for LIVE games: ${ttl/1000}s`, { cacheKey });
        } else {
          // Use the first fixture's time to determine TTL
          const firstFixture = fixtures[0];
          ttl = getDynamicTTL(firstFixture, 'fixtures');
          logCache('info', `Dynamic TTL based on fixture timing: ${ttl/1000}s`, { cacheKey });
        }
      } else {
        // Fallback to static TTL
        ttl = isLive ? getTTLForDataType('fixtures', true) : getTTLForDataType('fixtures');
      }

      // Store with calculated TTL
      await this.cache.set(cacheKey, result, ttl);
      return result;
    };

    // Check cache first, but if miss, use dynamic TTL on fetch
    const cachedData = await this.cache.get(
      cacheKey,
      fetchWithDynamicTTL,
      getTTLForDataType('fixtures', isLive) // Initial TTL for cache check
    );

    return cachedData;
  }

  /**
   * Get odds with RTDB caching and dynamic TTL
   */
  async getOdds(fixture = null, league = null, season = null, date = null,
                timezone = null, page = 1, bookmaker = null, bet = null) {
    if (!fixture) {
      // Don't cache queries without specific fixture
      return this.baseService.getOdds(
        fixture, league, season, date, timezone, page, bookmaker, bet
      );
    }

    const cacheKey = `${CACHE_PATHS.ODDS_PREMATCH}/${getCacheKey('fixture', fixture)}`;

    // Wrapper with dynamic TTL for odds
    const fetchWithDynamicTTL = async () => {
      const result = await this.baseService.getOdds(
        fixture, league, season, date, timezone, page, bookmaker, bet
      );

      // Determine TTL based on fixture data (if available in response)
      let ttl = getTTLForDataType('odds');
      if (RTDB_FEATURES.USE_DYNAMIC_TTL && result?.response?.[0]?.fixture) {
        ttl = getDynamicTTL(result.response[0].fixture, 'odds');
        logCache('info', `Dynamic TTL for odds: ${ttl/1000}s`, { fixture });
      }

      await this.cache.set(cacheKey, result, ttl);
      return result;
    };

    return this.cache.get(
      cacheKey,
      fetchWithDynamicTTL,
      getTTLForDataType('odds')
    );
  }

  /**
   * Get live odds with RTDB caching - ultra fresh for live games
   */
  async getLiveOdds(fixture = null, league = null, bet = null) {
    if (!fixture) {
      // Don't cache queries without specific fixture
      return this.baseService.getLiveOdds(fixture, league, bet);
    }

    const cacheKey = `${CACHE_PATHS.ODDS_LIVE}/${getCacheKey('fixture', fixture)}`;

    // Live odds always use short TTL (15-30 seconds)
    const ttl = 15 * 1000; // 15 seconds for live odds

    return this.cache.get(
      cacheKey,
      () => this.baseService.getLiveOdds(fixture, league, bet),
      ttl
    );
  }

  /**
   * Get comprehensive fixture odds with RTDB caching
   */
  async getComprehensiveFixtureOdds(fixtureId) {
    // This combines multiple API calls, cache each separately
    const [preMatch, live, bookmakers, bets, liveBets] = await Promise.all([
      this.getOdds(fixtureId),
      this.getLiveOdds(fixtureId),
      this.getBookmakers(),
      this.getBets(),
      this.getLiveBets()
    ]);

    return {
      preMatch: preMatch.response || [],
      live: live.response || [],
      bookmakers: bookmakers.response || [],
      betTypes: bets.response || [],
      liveBetTypes: liveBets.response || [],
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Get standings with RTDB caching
   */
  async getStandings(league = null, season, team = null) {
    if (!league || !season) {
      return this.baseService.getStandings(league, season, team);
    }

    const cacheKey = `${CACHE_PATHS.STANDINGS}/${getCacheKey('standings', league, season)}`;
    const ttl = getTTLForDataType('standings');

    return this.cache.get(
      cacheKey,
      () => this.baseService.getStandings(league, season, team),
      ttl
    );
  }

  /**
   * Get bookmakers (cached for 24 hours)
   */
  async getBookmakers(id = null, search = null) {
    const cacheKey = `${CACHE_PATHS.STATIC}/bookmakers`;
    const ttl = getTTLForDataType('bookmakers');

    return this.cache.get(
      cacheKey,
      () => this.baseService.getBookmakers(id, search),
      ttl
    );
  }

  /**
   * Get bet types (cached for 24 hours)
   */
  async getBets(id = null, search = null) {
    const cacheKey = `${CACHE_PATHS.STATIC}/bets`;
    const ttl = getTTLForDataType('teams');

    return this.cache.get(
      cacheKey,
      () => this.baseService.getBets(id, search),
      ttl
    );
  }

  /**
   * Get live bet types (cached for 24 hours)
   */
  async getLiveBets(id = null, search = null) {
    const cacheKey = `${CACHE_PATHS.STATIC}/liveBets`;
    const ttl = getTTLForDataType('teams');

    return this.cache.get(
      cacheKey,
      () => this.baseService.getLiveBets(id, search),
      ttl
    );
  }

  /**
   * Get leagues (cached for 24 hours)
   */
  async getLeagues(id = null, name = null, country = null, code = null, 
                   season = null, team = null, type = null, current = null, 
                   search = null, last = null) {
    const cacheKey = `${CACHE_PATHS.STATIC}/leagues/${getCacheKey('leagues', country || 'all')}`;
    const ttl = getTTLForDataType('leagues');

    return this.cache.get(
      cacheKey,
      () => this.baseService.getLeagues(
        id, name, country, code, season, team, type, current, search, last
      ),
      ttl
    );
  }

  /**
   * Clear cache for specific data type
   */
  async clearCache(type = 'all') {
    if (type === 'all') {
      await this.cache.clearAll();
      logCache('info', 'Cleared all football cache');
    } else {
      const paths = {
        'fixtures': CACHE_PATHS.FIXTURES,
        'odds': CACHE_PATHS.ODDS_PREMATCH,
        'live-odds': CACHE_PATHS.ODDS_LIVE,
        'standings': CACHE_PATHS.STANDINGS
      };
      
      if (paths[type]) {
        await this.cache.clear(paths[type]);
        logCache('info', `Cleared ${type} cache`);
      }
    }
  }

  /**
   * Pre-warm cache for popular data
   */
  async warmCache(date = null) {
    if (!RTDB_FEATURES.USE_RTDB_CACHE) return;

    const targetDate = date || this.baseService.getTodayDate();
    const popularLeagues = [39, 140, 78, 135, 61, 253, 71]; // EPL, La Liga, etc.

    logCache('info', 'Warming cache for popular leagues', { date: targetDate });

    // Warm fixtures cache
    const warmPromises = popularLeagues.map(leagueId => 
      this.getFixtures(null, null, null, targetDate, leagueId, 2025)
        .catch(err => console.error(`Failed to warm league ${leagueId}:`, err))
    );

    await Promise.allSettled(warmPromises);
    logCache('info', 'Cache warming complete');
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    return this.cache.getStats();
  }

  /**
   * Check cache connection status
   */
  isConnected() {
    return this.cache.isConnected();
  }
}

// Create singleton instance that wraps all FootballService methods
const rtdbFootballService = new RTDBFootballService();

// Proxy all other FootballService methods not explicitly overridden
const handler = {
  get(target, prop) {
    // If method exists on RTDBFootballService, use it
    if (prop in target) {
      return target[prop];
    }
    
    // Otherwise, proxy to base FootballService
    if (prop in target.baseService) {
      const value = target.baseService[prop];
      if (typeof value === 'function') {
        return value.bind(target.baseService);
      }
      return value;
    }
    
    return undefined;
  }
};

const proxiedService = new Proxy(rtdbFootballService, handler);

// Export for browser console debugging
if (typeof window !== 'undefined' && RTDB_FEATURES.ENABLE_DEBUG) {
  window.RTDBFootball = {
    warmCache: (date) => proxiedService.warmCache(date),
    clearCache: (type) => proxiedService.clearCache(type),
    stats: () => proxiedService.getCacheStats(),
    isConnected: () => proxiedService.isConnected()
  };
}

export default proxiedService;